/* eslint-disable react/prop-types */
import * as React from "react";
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import { Link, useNavigate } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import Logo from "../assets/Logo.png";
import LogoutDialog from "./LogoutDialog";
import { APP_NAME, BANK_NAME, colors, HEADER_HEIGHT } from "../brand";

export default function Header({
  sidebarOpen,
  onToggleSidebar,
  pendingCount = 0,
  deletionCount = 0,
  canApprove = false,
}) {
  const alertCount = pendingCount + deletionCount;
  const navigate = useNavigate();
  const [notifyAnchor, setNotifyAnchor] = React.useState(null);
  const [userAnchor, setUserAnchor] = React.useState(null);
  const [logoutOpen, setLogoutOpen] = React.useState(false);

  const user = React.useMemo(() => {
    try {
      return JSON.parse(secureLocalStorage.getItem("profile"));
    } catch {
      return null;
    }
  }, []);
  const firstName = user?.results?.firstName || "";
  const lastName = user?.results?.lastName || "";
  const initials = ((firstName[0] || "U") + (lastName[0] || "")).toUpperCase();

  const goTo = (to) => {
    setNotifyAnchor(null);
    setUserAnchor(null);
    navigate(to);
  };

  return (
    <Box
      component="header"
      sx={{
        flexShrink: 0,
        zIndex: 1100,
        height: HEADER_HEIGHT,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: { xs: 1, sm: 2 },
        backgroundColor: colors.surface,
        borderBottom: `1px solid ${colors.border}`,
      }}
    >
      <Tooltip title={sidebarOpen ? "Hide menu" : "Show menu"}>
        <IconButton
          aria-label={sidebarOpen ? "Hide menu" : "Show menu"}
          onClick={onToggleSidebar}
          sx={{ color: colors.primary }}
        >
          {sidebarOpen ? <MenuOpenIcon /> : <MenuIcon />}
        </IconButton>
      </Tooltip>

      <Box
        component={Link}
        to="/reconciliation/"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          textDecoration: "none",
          color: colors.textPrimary,
        }}
      >
        <img src={Logo} alt="" width={34} height={34} style={{ objectFit: "contain" }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontWeight: 800, fontSize: { xs: 13, sm: 16 }, lineHeight: 1.15 }}
          >
            {BANK_NAME}
          </Typography>
          <Typography
            noWrap
            sx={{ fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.2 }}
          >
            {APP_NAME}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1 }} />

      {canApprove && (
        <Tooltip title="Notifications">
          <IconButton
            aria-label={`Notifications, ${alertCount} waiting`}
            onClick={(e) => setNotifyAnchor(e.currentTarget)}
          >
            <Badge color="error" badgeContent={alertCount} max={99}>
              <NotificationsNoneOutlinedIcon sx={{ color: colors.primary }} />
            </Badge>
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Your account">
        <IconButton
          aria-label="Your account"
          onClick={(e) => setUserAnchor(e.currentTarget)}
          sx={{ p: 0.5 }}
        >
          <Avatar
            sx={{
              width: 34,
              height: 34,
              fontSize: 14,
              fontWeight: 800,
              bgcolor: colors.primary,
            }}
          >
            {initials}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={notifyAnchor}
        open={Boolean(notifyAnchor)}
        onClose={() => setNotifyAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { borderRadius: "12px", minWidth: 280 } }}
      >
        <Typography sx={{ px: 2, py: 1, fontWeight: 800 }}>
          Notifications
        </Typography>
        <Divider />
        {pendingCount > 0 && (
          <MenuItem onClick={() => goTo("/reconciliation/approvals")}>
            <Typography sx={{ fontSize: 14, whiteSpace: "normal" }}>
              <strong>{pendingCount}</strong> report
              {pendingCount > 1 ? "s are" : " is"} waiting for your approval
            </Typography>
          </MenuItem>
        )}
        {deletionCount > 0 && (
          <MenuItem onClick={() => goTo("/reconciliation/delete-reports")}>
            <Typography sx={{ fontSize: 14, whiteSpace: "normal" }}>
              <strong>{deletionCount}</strong> deletion request
              {deletionCount > 1 ? "s are" : " is"} waiting for a decision
            </Typography>
          </MenuItem>
        )}
        {alertCount === 0 && (
          <Typography sx={{ px: 2, py: 1.5, fontSize: 14, color: colors.textSecondary }}>
            Nothing needs your attention.
          </Typography>
        )}
      </Menu>

      <Menu
        anchorEl={userAnchor}
        open={Boolean(userAnchor)}
        onClose={() => setUserAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { borderRadius: "12px", minWidth: 220 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontWeight: 800 }}>
            {firstName} {lastName}
          </Typography>
          <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
            {user?.results?.reconciliation || "User"}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => goTo("/reconciliation/user-page")}>
          <ListItemIcon>
            <ManageAccountsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          My account
        </MenuItem>
        <MenuItem
          onClick={() => {
            setUserAnchor(null);
            setLogoutOpen(true);
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Log out
        </MenuItem>
      </Menu>

      <LogoutDialog open={logoutOpen} onClose={() => setLogoutOpen(false)} />
    </Box>
  );
}
